// ─────────────────────────────────────────────────────────────────────────────
// FACE DETECTOR — MediaPipe FaceLandmarker (478 facial landmarks)
// Runtime: browser WASM via CDN (no server required)
// Degrades gracefully: if load fails, returns null landmarks
// ─────────────────────────────────────────────────────────────────────────────
import {
  FaceLandmarker,
  FilesetResolver,
  type FaceLandmarkerResult,
} from "@mediapipe/tasks-vision";

export type { FaceLandmarkerResult };

export interface NormalizedFaceLandmark {
  x: number; // 0–1 normalized
  y: number;
  z: number;
}

// MediaPipe Face landmark indices (key facial features)
export const FACE_IDX = {
  // Eyes
  LEFT_EYE_INNER: 133,
  LEFT_EYE: 145,
  LEFT_EYE_OUTER: 163,
  RIGHT_EYE_INNER: 362,
  RIGHT_EYE: 374,
  RIGHT_EYE_OUTER: 386,

  // Eyebrows
  LEFT_EYEBROW_INNER: 46,
  LEFT_EYEBROW_OUTER: 52,
  RIGHT_EYEBROW_INNER: 276,
  RIGHT_EYEBROW_OUTER: 282,

  // Nose
  NOSE_TIP: 1,
  NOSE_BRIDGE: 6,

  // Mouth
  MOUTH_LEFT: 61,
  MOUTH_RIGHT: 291,
  MOUTH_TOP: 0,
  MOUTH_BOTTOM: 17,

  // Jaw/Chin
  CHIN: 152,
  LEFT_JAW: 58,
  RIGHT_JAW: 288,

  // Ears
  LEFT_EAR: 234,
  RIGHT_EAR: 454,
} as const;

export let _faceLandmarker: FaceLandmarker | null = null;
let _faceLoading = false;
let _faceFailed = false;

export async function getFaceLandmarker(): Promise<FaceLandmarker | null> {
  if (_faceLandmarker) return _faceLandmarker;
  if (_faceFailed) return null;
  if (_faceLoading) {
    // Wait for existing load
    await new Promise<void>((res) => {
      const iv = setInterval(() => {
        if (!_faceLoading) {
          clearInterval(iv);
          res();
        }
      }, 100);
    });
    return _faceLandmarker;
  }

  _faceLoading = true;
  try {
    const vision = await FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
    );

    _faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath:
          "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
        delegate: "GPU",
      },
      outputFaceBlendshapes: false,
      outputFacialTransformationMatrixes: false,
      runningMode: "VIDEO",
      numFaces: 1, // Track only one face
    });

    console.log("✅ FaceLandmarker loaded");
    return _faceLandmarker;
  } catch (err) {
    console.warn("❌ FaceLandmarker failed to load:", err);
    _faceFailed = true;
    return null;
  } finally {
    _faceLoading = false;
  }
}

export async function detectFaces(
  video: HTMLVideoElement
): Promise<FaceLandmarkerResult | null> {
  const landmarker = await getFaceLandmarker();
  if (!landmarker) return null;

  try {
    const result = await landmarker.detectForVideo(video, performance.now());
    return result;
  } catch (err) {
    console.warn("Face detection error:", err);
    return null;
  }
}

export function extractFaceGeometry(
  faceResult: FaceLandmarkerResult
): NormalizedFaceLandmark[] | null {
  if (!faceResult.faceLandmarks || faceResult.faceLandmarks.length === 0) {
    return null;
  }

  return faceResult.faceLandmarks[0]; // Return first detected face
}

export function getFaceBoundingBox(
  faceLandmarks: NormalizedFaceLandmark[]
): { x: number; y: number; width: number; height: number } | null {
  if (!faceLandmarks || faceLandmarks.length === 0) return null;

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const landmark of faceLandmarks) {
    minX = Math.min(minX, landmark.x);
    minY = Math.min(minY, landmark.y);
    maxX = Math.max(maxX, landmark.x);
    maxY = Math.max(maxY, landmark.y);
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * Debug visualization: Draw face bounding box and landmarks on canvas
 * @param canvas - Canvas to draw on
 * @param faceResult - Face detection result
 * @param videoWidth - Video width for scaling
 * @param videoHeight - Video height for scaling
 */
export function drawFaceDebugOverlay(
  canvas: HTMLCanvasElement,
  faceResult: FaceLandmarkerResult,
  videoWidth: number,
  videoHeight: number
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Clear previous drawings
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  if (!faceResult.faceLandmarks || faceResult.faceLandmarks.length === 0) {
    return;
  }

  const faceLandmarks = faceResult.faceLandmarks[0];
  const bbox = getFaceBoundingBox(faceLandmarks);

  if (bbox) {
    // Draw bounding box
    ctx.strokeStyle = '#00FF00';
    ctx.lineWidth = 2;
    ctx.strokeRect(
      bbox.x * videoWidth,
      bbox.y * videoHeight,
      bbox.width * videoWidth,
      bbox.height * videoHeight
    );

    // Draw bounding box label
    ctx.fillStyle = '#00FF00';
    ctx.font = '12px Arial';
    ctx.fillText(
      'Face Detected',
      bbox.x * videoWidth,
      bbox.y * videoHeight - 5
    );
  }

  // Draw key facial landmarks
  ctx.fillStyle = '#FF0000';
  ctx.strokeStyle = '#FF0000';
  ctx.lineWidth = 1;

  // Draw eyes
  const keyPoints = [
    FACE_IDX.LEFT_EYE,
    FACE_IDX.RIGHT_EYE,
    FACE_IDX.NOSE_TIP,
    FACE_IDX.MOUTH_LEFT,
    FACE_IDX.MOUTH_RIGHT,
  ];

  for (const idx of keyPoints) {
    if (idx < faceLandmarks.length) {
      const landmark = faceLandmarks[idx];
      const x = landmark.x * videoWidth;
      const y = landmark.y * videoHeight;

      // Draw point
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, 2 * Math.PI);
      ctx.fill();

      // Draw index label
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '8px Arial';
      ctx.fillText(idx.toString(), x + 5, y - 5);
      ctx.fillStyle = '#FF0000';
    }
  }

  // Draw facial outline (subset of landmarks)
  ctx.strokeStyle = '#FFFF00';
  ctx.lineWidth = 1;
  ctx.beginPath();

  // Draw jawline
  const jawPoints = [58, 172, 136, 150, 149, 176, 148, 152, 377, 400, 378, 379, 365, 397, 288];
  for (let i = 0; i < jawPoints.length; i++) {
    const idx = jawPoints[i];
    if (idx < faceLandmarks.length) {
      const landmark = faceLandmarks[idx];
      const x = landmark.x * videoWidth;
      const y = landmark.y * videoHeight;

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
  }
  ctx.stroke();
}